<style scoped>
/* Styles remain consistent */
::-webkit-scrollbar {
    width: 10px;
}
::-webkit-scrollbar-thumb {
    background: rgba(191, 188, 188, 0.6);
}
.required_err {
    color: rgba(255, 0, 0);
}
.br {
    color: rgba(109, 103, 164);
}
.elevation-1 tr:nth-of-type(even) {
    background-color: rgba(0, 0, 0, .03) !important;
}
.action-btn-spacing {
    margin-left: 8px;
}
.v-progress-linear {
    height: 2px !important;
}
.top-combobox {
    max-width: 400px;
    margin-right: 16px;
}
</style>

<template>
<div class="prefix_map_entries">

    <!-- Main Data Table -->
    <v-data-table
        :headers="entry_headers"
        :items="current_entries"
        :search="search"
        sort-by="p"
        class="elevation-1"
    >
        <!-- Top Slot: Toolbars -->
        <template v-slot:top>

            <!-- Toolbar 1: Title and Add Button/Dialog -->
            <v-toolbar flat>
                <v-toolbar-title><h2 class="br">Prefix Map Entries</h2></v-toolbar-title>
                <v-spacer></v-spacer>

                <!-- Add Entry Dialog -->
                <v-dialog v-model="dialog_entry" max-width="600px">
                    <template v-slot:activator="{ props }">
                        <v-btn
                            @click="new_entry"
                            color="primary"
                            dark
                            class="mb-2 action-btn-spacing"
                            v-bind="props"
                            :disabled="loading_maps || loading_entries"
                        >
                            Add entry
                        </v-btn>
                    </template>
                    <ValidationObserver ref="observer_entry" v-slot="{ invalid }">
                        <v-card>
                            <v-form @submit.prevent="save_entry">
                                <v-card-title>
                                    <span class="headline">{{ entry_form_title }}</span>
                                </v-card-title>
                                <v-card-text>
                                    <v-container style="max-height: 520px; overflow-y: auto;">
                                        <v-row>
                                            <!-- Inputs remain the same -->
                                            <v-col cols="12">
                                                <ValidationProvider :rules="prefix_validation_rules" v-slot="{ errors }">
                                                    <v-text-field
                                                        v-model="edited_entry.prefix"
                                                        label="Prefix"
                                                        :hint="`Enter a valid ${selected_map_type || 'IP'} prefix with CIDR mask`"
                                                        persistent-hint
                                                    ></v-text-field>
                                                    <span class="required_err">{{ errors[0] }}</span>
                                                </ValidationProvider>
                                            </v-col>
                                            <v-col cols="12" sm="6">
                                                <ValidationProvider rules="required|numeric" v-slot="{ errors }">
                                                    <v-text-field
                                                        v-model.number="edited_entry.value"
                                                        label="Value"
                                                        type="number"
                                                    ></v-text-field>
                                                    <span class="required_err">{{ errors[0] }}</span>
                                                </ValidationProvider>
                                            </v-col>

                                            <v-col cols="12" sm="6">
                                                <ValidationProvider rules="" v-slot="{ errors }">
                                                    <v-combobox
                                                        v-model="edited_entry.flags"
                                                        :items="['', 'persistent']"
                                                        label="Flags (Optional)"
                                                        dense
                                                        clearable
                                                        hide-details="auto"
                                                    ></v-combobox>
                                                    <!-- Error display remains the same -->
                                                    <span class="required_err">{{ errors[0] }}</span>
                                                </ValidationProvider>
                                            </v-col>                                            
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn color="blue darken-1" text @click="close_entry_dialog">Cancel</v-btn>
                                    <v-btn color="blue darken-1" :disabled="invalid" text type="submit">Save</v-btn>
                                </v-card-actions>
                            </v-form>
                        </v-card>
                    </ValidationObserver>
                </v-dialog>

            </v-toolbar>

            <!-- Toolbar 2: Prefix Map Selection -->
            <v-toolbar flat>
                <v-select
                    v-model="selected_map_id"
                    :items="map_selector_items"
                    label="Prefix Map"
                    item-text="text"
                    item-value="value"
                    :loading="loading_maps"
                    clearable
                    dense
                    hide-details
                    class="top-combobox"
                    @change="on_map_change"
                ></v-select>
                <v-spacer></v-spacer>
            </v-toolbar>

            <!-- Toolbar 3: Search Field -->
            <v-toolbar flat>
                <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search Entries"
                    single-line
                    hide-details
                    dense
                    :disabled="!selected_map_id || loading_entries"
                ></v-text-field>
            </v-toolbar>

        </template> <!-- End v-slot:top -->

        <!-- Table Item Slots -->
        <template v-slot:item.actions="{ item }">
            <v-tooltip top>
                <template v-slot:activator="{ props }">
                    <v-icon
                        small
                        @click="delete_entry(item)"
                        v-bind="props"
                        :disabled="loading_entries"
                    >
                        mdi-delete
                    </v-icon>
                </template>
                <span>Delete entry '{{ item.p }}'</span>
            </v-tooltip>
        </template>

        <template v-slot:no-data>
            <span v-if="loading_maps">Loading maps...</span>
            <span v-else-if="!selected_map_id">Please select a Prefix Map to view entries.</span>
            <span v-else-if="loading_entries">Loading entries for map {{ selected_map_id }}...</span>
            <span v-else>No entries found for this map.</span>
        </template>

        <template v-slot:progress>
            <v-progress-linear
                :active="loading_entries || loading_maps"
                indeterminate
                color="primary"
                absolute
                bottom
            ></v-progress-linear>
        </template>

    </v-data-table> <!-- End Main Data Table -->

</div>
</template>

<script>
import api_client from '../services/api.js';
import { ValidationProvider, extend } from 'vee-validate';
import { ValidationObserver } from 'vee-validate';
import { required, numeric } from 'vee-validate/dist/rules';
import "../validation_rules.js";

export default {
    name: "prefix_map_entries",
    components: { ValidationProvider, ValidationObserver },
    data: () => ({
        all_maps: [], // Holds map list {id, type} ONLY
        current_entries: [], // Holds entries for the SELECTED map
        selected_map_id: null,
        loading_maps: false,
        loading_entries: false, // Specific loading state for entries
        search: '',

        // Entry Table Headers (remain same)
        entry_headers: [
            { text: 'Prefix', value: 'p', align: 'start' },
            { text: 'Value', value: 'v' },
            { text: 'Flags', value: 'f' },
            { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
        ],

        // Add/Edit Dialog State (remain same)
        dialog_entry: false,
        edited_entry_index: -1,
        edited_entry: {
            prefix: '',
            value: null,
            flags: '',
        },
        default_entry: {
            prefix: '',
            value: 1,
            flags: '',
        },
    }),

    computed: {
        prefix_validation_rules() {
            const baseRule = 'required';
            const mapType = this.selected_map_type; // Get the current type

            if (mapType === 'ipv4') {
                return `${baseRule}|ipv4_prefix`;
            } 
            else if (mapType === 'ipv6') {
                return `${baseRule}|ipv6_prefix`; // Combine rules for IPv6
            }
        },

        // Formats map list for dropdown (remains same)
        map_selector_items() {
            return this.all_maps.map(map => ({
                text: `ID: ${map.id} (${map.type})`,
                value: map.id,
            }));
        },

        // Finds the selected map object from the list (still needed for type)
        selected_map_object() {
            if (!this.selected_map_id || !this.all_maps.length) {
                return null;
            }
            const numeric_map_id = parseInt(this.selected_map_id, 10);
            return this.all_maps.find(map => map.id === numeric_map_id);
        },

        // Gets the type of the selected map (remains same)
        selected_map_type() {
            return this.selected_map_object?.type || null;
        },

        // Title for the Add/Edit dialog (remains same)
        entry_form_title() {
            return `Add Entry to prefix map id ${this.selected_map_id}`
        },

        // NOTE: The 'entries' computed property is removed as we now use 'current_entries' data property directly
    },

    watch: {
        // Reset dialog state when closed (remains same)
        dialog_entry(val) {
            val || this.close_entry_dialog();
        },
    },

    created() {
        this.fetch_all_maps(); // Load ONLY map list on component creation
    },

    methods: {
        // Fetches the list of map IDs and types ONLY
        async fetch_all_maps() {
            this.loading_maps = true;
            this.all_maps = []; // Clear previous maps
            this.current_entries = []; // Clear entries when maps reload
            this.selected_map_id = null; // Deselect map
            try {
                const response = await api_client.run_get_query('/prefix_map/show');

                if (!response || !response.data) {
                    throw new Error("Invalid response structure from server.");
                }
                if (response.data.hasOwnProperty("err_msg") || !response.data.hasOwnProperty("prefix_maps")) {
                    this.$fire({title: "Command failed",
                        text: response.data.err_msg || "Missing prefix_maps data", type: "warning"});
                    return; // Keep all_maps empty
                }

                // Store only ID and type, ensure ID is number
                this.all_maps = response.data.prefix_maps.map(map => ({
                    id: parseInt(map.id, 10),
                    type: map.type
                }));
            }
            catch (error) {
                console.error("Error fetching prefix maps:", error);
                this.$fire({ title: "Error", text: error, type: "error" });
                // all_maps remains empty
            } 
            finally {
                this.loading_maps = false;
            }
        },

        // Triggered when a map is selected or cleared in the dropdown
        async on_map_change(selected_id) {
            this.search = ''; // Clear search
            this.current_entries = []; // Clear previous entries immediately

            if (selected_id) {
                const numeric_map_id = parseInt(selected_id, 10);
                if (!isNaN(numeric_map_id)) {
                    await this.fetch_map_entries(numeric_map_id); // Fetch entries for the new selection
                }
            }
            // If selected_id is null (cleared), entries are already cleared
        },

        // Fetches entries for a SPECIFIC map ID
        async fetch_map_entries(map_id) {
            if (map_id === null || map_id === undefined) {
                return;
            }
            this.loading_entries = true;
            this.current_entries = []; // Ensure starting fresh
            try {
                const payload = { map_id: map_id };
                // Use POST as defined in the backend snippet
                const response = await api_client.run_post_query('/prefix_map/entries', payload);

                if (!response || !response.data) {
                     throw new Error("Invalid response structure from entry API.");
                }
                 // Check for backend errors specific to the entry API response
                if (response.data.hasOwnProperty("err_msg")) {
                     this.$fire({title: "Entry Fetch Failed", text: response.data.err_msg, type: "warning"});
                     return; // Keep current_entries empty
                }

                // Assign the fetched prefixes to our data property
                this.current_entries = response.data.prefixes || [];

            }
            catch (error) {
                console.error(`Error fetching entries for map ${map_id}:`, error);
                this.$fire({ title: "Error", text: error, type: "error" });
                // Keep current_entries empty on error
            }
            finally {
                this.loading_entries = false;
            }
        },

        // Prepare dialog (remains mostly same)
        new_entry() {
            if (!this.selected_map_id) {
                this.$fire({ title: "Warning", text: "Please select a Prefix Map first.", type: "warning" });
                return;
            }
            this.edited_entry_index = -1;
            this.edited_entry = { ...this.default_entry };
            this.dialog_entry = true;
            this.$nextTick(() => {
                this.$refs.observer_entry.reset();
            });
        },

        // Close dialog (remains same)
        close_entry_dialog() {
            this.dialog_entry = false;
            this.$nextTick(() => {
                this.edited_entry = { ...this.default_entry };
                this.edited_entry_index = -1;
                this.$refs.observer_entry.reset();
            });
        },

        // Save a new entry
        async save_entry() {
            if (!this.selected_map_id) {
                return;
            }
            const numeric_map_id = parseInt(this.selected_map_id, 10);
            const entry_value = parseInt(this.edited_entry.value, 10);

            if (isNaN(numeric_map_id) || isNaN(entry_value)) {
                this.$fire({ title: "Internal Error", text: "Invalid map ID or entry value.", type: "error" });
                return;
            }

            const payload = {
                map_id: numeric_map_id,
                prefix: this.edited_entry.prefix,
                value: entry_value,
                ...(this.edited_entry.flags && { flags: this.edited_entry.flags })
            };

            if (this.edited_entry_index === -1) {
                try {
                    const response = await api_client.run_post_query('/prefix_map/add', payload);

                    if (response.data && response.data.hasOwnProperty("err_msg")) {
                        this.$fire({ title: "Command failed", text: response.data.err_msg, type: "warning" });
                        return;
                    }

                    // Add locally to current_entries
                    const new_entry = {
                        p: this.edited_entry.prefix,
                        v: entry_value,
                        f: this.edited_entry.flags || ""
                    };
                    this.current_entries.push(new_entry);

                    this.close_entry_dialog();

                }
                catch (error) {
                    console.error("Error adding prefix map entry:", error);
                    this.$fire({title: "Command failed", text: error, type: "error"});
                } 
            }
        },

        // Initiate deletion (remains mostly same)
        delete_entry(item) {
            if (!this.selected_map_id) {
                return;
            }
            const numeric_map_id = parseInt(this.selected_map_id, 10);
            this.$confirm(`Are you sure you want to delete the entry '${item.p}' from map ${numeric_map_id}?`)
                .then((res) => {
                    if (res) {
                        this.delete_entry_confirm(item);
                    }
                });
        },

        // Confirm and execute deletion
        async delete_entry_confirm(item_to_delete) {
            if (!this.selected_map_id) {
                return;
            }
            const numeric_map_id = parseInt(this.selected_map_id, 10);

            const payload = {
                map_id: numeric_map_id,
                ipv4_prefix: item_to_delete.p // Key matches backend model
            };

            try {
                const response = await api_client.run_post_query('/prefix_map/delete', payload);

                if (response.data && response.data.hasOwnProperty("err_msg")) {
                    this.$fire({ title: "Command failed", text: response.data.err_msg, type: "warning" });
                    return;
                }

                // Remove locally from current_entries
                const index_to_delete = this.current_entries.findIndex(entry => entry.p === item_to_delete.p);
                if (index_to_delete !== -1) {
                    this.current_entries.splice(index_to_delete, 1);
                }
                else {
                    console.warn("Could not find entry to delete locally in current_entries.");
                    // Maybe force refresh the entries for the current map?
                    // await this.fetch_map_entries(numeric_map_id);
                }

            }
            catch (error) {
                console.error("Error deleting prefix map entry:", error);
                this.$fire({title: "Command failed", text: error, type: "warning"});
            } 
        },
    }
};
</script>
